import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from '../core/auth/auth.service';

/**
 * Role flags for UI gating — port of Flutter's `AdminGate` (`lib/utils/admin_gate.dart`).
 *
 * `GET user/roles/` is the source of truth; the access token's role claims are
 * only a fallback when that request fails, and a fallback answer is retried on
 * the next login rather than cached as final (a token can carry just the
 * Keycloak defaults). Flags are false until resolved and reset on logout.
 */
@Injectable({ providedIn: 'root' })
export class UserRolesService {
  private static readonly ADMIN_ROLES = ['spectra.admin', 'admin', 'spectra_admin', 'superadmin', 'realm-admin'];
  private static readonly AMBASSADOR_ROLES = ['spectra.ambassador', 'ambassador', 'spectra_ambassador'];

  private readonly canAccessReplaySubject = new BehaviorSubject<boolean>(false);

  /** Multi-trip replay is enabled for ambassadors (and admins), like `canAccessHomeTripReplay`. */
  readonly canAccessReplay$: Observable<boolean> = this.canAccessReplaySubject.asObservable();

  constructor(private http: HttpClient, private auth: AuthService) {
    this.auth.isLoggedIn$.pipe(distinctUntilChanged()).subscribe((loggedIn) => {
      if (loggedIn) this.fetch();
      else this.publish([]);
    });
  }

  get canAccessReplay(): boolean {
    return this.canAccessReplaySubject.value;
  }

  private fetch(): void {
    this.http.get<{ roles?: unknown }>(`${environment.apiUrl}/user/roles/`).subscribe({
      next: (res) => this.publish(this.toStrings(res?.roles)),
      // Network/server failure: fall back to whatever the token itself claims.
      error: () => this.publish(this.rolesFromToken(this.auth.getAccessToken())),
    });
  }

  private publish(roles: string[]): void {
    const lower = roles.map((r) => r.trim().toLowerCase());
    const has = (wanted: string[]) => lower.some((r) => wanted.includes(r));
    this.canAccessReplaySubject.next(
      has(UserRolesService.ADMIN_ROLES) || has(UserRolesService.AMBASSADOR_ROLES),
    );
  }

  private toStrings(value: unknown): string[] {
    return Array.isArray(value) ? value.map((v) => String(v)) : [];
  }

  /** Same claim sources Flutter reads: realm_access, resource_access.<client>, roles. */
  private rolesFromToken(token: string | null): string[] {
    if (!token) return [];
    try {
      const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const claims = JSON.parse(atob(payload));
      const out = new Set<string>();
      const add = (list: unknown, client?: string) => {
        for (const r of this.toStrings(list)) {
          const role = r.trim();
          if (!role) continue;
          out.add(role);
          if (client) out.add(`${client}.${role}`);
        }
      };
      add(claims?.realm_access?.roles);
      for (const [client, v] of Object.entries<any>(claims?.resource_access ?? {})) add(v?.roles, client);
      add(claims?.roles);
      return [...out];
    } catch {
      return [];
    }
  }
}
